import React, {
  createContext,
  ReactNode,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react'
import { fullscreenApi } from '@sketch/utils'

/**
 * Context for values and methods that are global to components within the
 * PrototypeView.
 */
type PrototypeViewContext = {
  /**
   * Whether fullscreen mode is active. Note that fullscreen mode means either
   * just a hidden header for unsupported browsers, or a hidden header _and_
   * native fullscreen mode active.
   */
  isFullscreen: boolean
  /**
   * Enter fullscreen mode.
   */
  enterFullscreen: () => void
  /**
   * Exit fullscreen mode.
   */
  exitFullscreen: () => void
  /**
   * Whether the exit fullscreen button should be visible. There are a few subtleties
   * here - we don't show this button by default when the user enters fullscreen
   * mode. We only want to show it if the user clicks outside of hotspots, and
   * if the user isn't in a native fullscreen mode (because in this mode the browser
   * will usually provide its own UI to exit fullscreen mode).
   */
  showExitFullscreenButton: boolean
  /**
   * Hide the exit fullscreen button.
   */
  toggleOffExitFullscreenButton: () => void
  /**
   * Callback that can be passed into the prototype player, it defines behaviour
   * to trigger when the user clicks outside any hotspot in the player.
   */
  onUnhandledClickInteraction: () => void
}

const context = createContext<PrototypeViewContext | undefined>(undefined)

type FullscreenProviderProps = {
  children: ReactNode
}

export function PrototypeViewContextProvider({
  children,
}: FullscreenProviderProps) {
  // Initialize the `isFullscreen` value based on whether the user is currently
  // in fullscreen mode already when they arrive on the page
  const [isFullscreen, setIsFullscreen] = useState(
    () => fullscreenApi.fullscreenElement() !== null
  )

  const [
    isExitFullscreenButtonToggled,
    setIsExitFullscreenButtonToggled,
  ] = useState(false)

  const enterFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.requestFullscreen(document.documentElement, {
        navigationUI: 'hide',
      })
    } catch (e) {
      // An error here means the browser doesn't support native fullscreen.
      // We can ignore it however, and set `isFullscreen` to `true` regardless
      // since we still want to hide the header
    }
    setIsFullscreen(true)
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.exitFullscreen()
    } catch (e) {
      // Ignore for same reason as above
    }
    setIsFullscreen(false)
  }, [])

  // User can enter and exit fullscreen mode without calling our functions by
  // using native browser buttons or shortcuts - so we still need to detect
  // this happening and sync our `active` value
  useEffect(() => {
    function onFullscreenChanged() {
      setIsFullscreen(Boolean(fullscreenApi.fullscreenElement()))
    }

    if (fullscreenApi.changeEvent) {
      document.addEventListener(fullscreenApi.changeEvent, onFullscreenChanged)
    }

    return () => {
      if (fullscreenApi.changeEvent) {
        document.removeEventListener(
          fullscreenApi.changeEvent,
          onFullscreenChanged
        )
      }
    }
  }, [])

  const onUnhandledClickInteraction = useCallback(() => {
    if (isFullscreen) {
      setIsExitFullscreenButtonToggled(value => !value)
    }
  }, [isFullscreen])

  const toggleOffExitFullscreenButton = useCallback(() => {
    setIsExitFullscreenButtonToggled(false)
  }, [])

  const showExitFullscreenButton =
    isFullscreen &&
    fullscreenApi.fullscreenElement() === null &&
    isExitFullscreenButtonToggled

  return (
    <context.Provider
      value={{
        isFullscreen,
        exitFullscreen,
        enterFullscreen,
        showExitFullscreenButton,
        onUnhandledClickInteraction,
        toggleOffExitFullscreenButton,
      }}
    >
      {children}
    </context.Provider>
  )
}

export function usePrototypeViewContext() {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'usePrototypeViewContext must be used within a PrototypeViewContextProvider'
    )
  }

  return contextValue
}
